import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ArticleDetail } from '../lib/api';
import { ARTICLE_CATEGORIES, ARTICLE_CONDITIONS, parseEuro } from '../lib/domain';
import { MdEditor } from '../mdeditor';
import { PhotoUploader } from '../photos';
import { MAX_PHOTOS, usePhotos } from '../usePhotos';
import { useToast } from '../hooks';
import { ErrorBox, Loading, PageHead } from '../ui';
import { leaveForm } from '../lib/nav';
import { useBarTitle } from '../barTitle';

export default function ArticleForm() {
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  useBarTitle(editId ? 'Anzeige bearbeiten' : 'Anzeige aufgeben'); // also while loading
  const src = useQuery({
    queryKey: ['article', editId],
    queryFn: () => api<ArticleDetail>(`/articles/${editId}`),
    enabled: !!editId,
  });
  if (src.error) return <ErrorBox error={src.error} />;
  if (editId && !src.data) return <Loading />;
  return <ArticleFormBody key={editId ?? 'new'} a={src.data} />;
}

function ArticleFormBody({ a }: { a?: ArticleDetail }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(a?.title ?? '');
  const [price, setPrice] = useState(a?.price != null ? String(a.price) : '');
  const [negotiable, setNegotiable] = useState(a?.negotiable ?? false);
  const [size, setSize] = useState(a?.size ?? '');
  const [category, setCategory] = useState<string>(a?.category ?? 'Sonstiges');
  const [condition, setCondition] = useState(a?.condition ?? '');
  const [description, setDescription] = useState(a?.description ?? '');
  const [notify, setNotify] = useState(true);
  const photos = usePhotos(a?.images);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const uploading = photos.uploading;
  const priceValue = parseEuro(price);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    if (title.trim().length < 2) return setError('Bitte gib an, was du verkaufst.');
    if (priceValue === undefined) return setError('Bitte den Preis als Zahl angeben, z. B. 120 oder 35,50.');
    if (uploading) return setError('Einen Moment – die Fotos werden noch hochgeladen.');
    const images = photos.payload();
    const body = {
      title: title.trim(),
      price: priceValue,
      negotiable: priceValue !== null && negotiable,
      size: size.trim(),
      category,
      condition,
      description,
      images,
    };
    setBusy(true);
    try {
      if (a) {
        await api(`/articles/${a.id}`, { method: 'PATCH', body });
        toast('Anzeige gespeichert');
        void qc.invalidateQueries({ queryKey: ['articles'] });
        void qc.invalidateQueries({ queryKey: ['article', a.id] });
        void qc.invalidateQueries({ queryKey: ['member'] });
        leaveForm(nav, `/flohmarkt/${a.id}`); // edit: back to the ad
      } else {
        const r = await api<{ id: number; notified: boolean }>('/articles', {
          method: 'POST',
          body: { ...body, notify },
        });
        toast(
          notify && !r.notified
            ? 'Anzeige veröffentlicht – Benachrichtigung übersprungen (zu viele in der letzten Stunde)'
            : notify
              ? 'Anzeige veröffentlicht – alle werden benachrichtigt (E-Mail mit Fotos und Push)'
              : 'Anzeige veröffentlicht',
        );
        void qc.invalidateQueries({ queryKey: ['articles'] });
        void nav(`/flohmarkt/${r.id}`, { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead title={a ? 'Anzeige bearbeiten' : 'Anzeige aufgeben'} />
      <form className="form panel" onSubmit={e => void submit(e)} noValidate style={{ maxWidth: 820 }}>
        <PhotoUploader photos={photos} label={`Fotos (bis zu ${MAX_PHOTOS} · das erste ist das Titelbild)`} />
        <label className="fl">
          <span>Was verkaufst du?</span>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            maxLength={90}
            placeholder="z. B. Neoprenanzug Sailfish, Herren Gr. L"
          />
        </label>
        <div className="grid3">
          <label className="fl">
            <span>Preis in €</span>
            <input
              value={price}
              onChange={e => setPrice(e.target.value)}
              inputMode="decimal"
              placeholder="leer = auf Anfrage"
              aria-invalid={priceValue === undefined || undefined}
            />
          </label>
          <div className="fl">
            <span>Verhandlungsbasis</span>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 0' }}>
              <span className="switch">
                <input
                  type="checkbox"
                  checked={negotiable && priceValue !== null}
                  disabled={priceValue === null}
                  onChange={e => setNegotiable(e.target.checked)}
                  aria-label="Verhandlungsbasis (VB)"
                />
                <span />
              </span>
              VB
            </label>
          </div>
          <label className="fl">
            <span>Größe</span>
            <input
              value={size}
              onChange={e => setSize(e.target.value)}
              maxLength={40}
              placeholder="z. B. 42, M, 56 cm"
            />
          </label>
        </div>
        <fieldset className="fl">
          <legend>Kategorie</legend>
          <div className="pills">
            {ARTICLE_CATEGORIES.map(c => (
              <label key={c} className="pill-opt">
                <input type="radio" name="cat" checked={category === c} onChange={() => setCategory(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="fl">
          <legend>Zustand</legend>
          <div className="pills">
            {ARTICLE_CONDITIONS.map(c => (
              <label key={c} className="pill-opt">
                <input type="radio" name="cond" checked={condition === c} onChange={() => setCondition(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <MdEditor
          id="art-desc"
          label="Beschreibung"
          value={description}
          onChange={setDescription}
          rows={7}
          maxLength={5000}
          placeholder="Zustand, Alter, Neupreis, Abholung …"
        />
        {!a && (
          <div className="notify-box">
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 600 }}>
              <span className="switch">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={e => setNotify(e.target.checked)}
                  aria-label="Mitglieder benachrichtigen"
                />
                <span />
              </span>
              Mitglieder benachrichtigen
            </label>
            <p>
              E-Mail mit Titelbild und allen weiteren Fotos und Push – an alle, die Flohmarkt-Benachrichtigungen wollen.
            </p>
          </div>
        )}
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void nav(-1)}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy || uploading}>
            {uploading ? 'Fotos werden hochgeladen …' : a ? 'Speichern' : 'Anzeige veröffentlichen'}
          </button>
        </div>
      </form>
    </>
  );
}
